import { useMemo, useState } from 'react';
import { Search, Smile, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { EmojiDisplay, useEmojiDirectory, type EmojiEntry } from './EmojiDisplay';
import { Popover } from '@/components/Popover';

/**
 * Popover-triggered picker that lets the user pick any custom emoji from any
 * guild the bot is in. Value flows out as the identifier "name:id" — that's
 * what our stored config + Discord button payloads use.
 *
 * If `allowUnicode` is set, a raw text input is exposed too so the user can
 * paste a unicode emoji instead. On clear, emits null.
 */

interface Props {
  value: string | null;
  onChange: (v: string | null) => void;
  allowUnicode?: boolean;
  placeholder?: string;
  buttonLabel?: string;
}

export function CustomEmojiPicker({
  value,
  onChange,
  allowUnicode = true,
  placeholder = 'Emoji wählen',
  buttonLabel,
}: Props) {
  return (
    <div className="inline-flex items-center gap-1">
      <Popover
        width={384}
        align="left"
        trigger={({ onClick, ref }) => (
          <div ref={ref} className="inline-block">
            <button
              type="button"
              onClick={onClick}
              className={cn(
                'flex h-10 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm hover:bg-accent'
              )}
            >
              {value ? (
                <EmojiDisplay value={value} size={20} />
              ) : (
                <Smile className="h-4 w-4 text-muted-foreground" />
              )}
              <span className={value ? '' : 'text-muted-foreground'}>
                {buttonLabel ?? (value ? 'Emoji ändern' : placeholder)}
              </span>
            </button>
          </div>
        )}
        content={(close) => (
          <PickerBody
            allowUnicode={allowUnicode}
            onPick={(v) => {
              onChange(v);
              close();
            }}
          />
        )}
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onChange(null)}
          title="Emoji entfernen"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

/**
 * Search + guild filter + emoji grid. `format`: "identifier" emits "name:id"
 * (config fields), "chat" emits "<:name:id>" / "<a:name:id>" (message text).
 */
export function PickerBody({
  onPick,
  allowUnicode,
  format = 'identifier',
}: {
  onPick: (v: string) => void;
  allowUnicode: boolean;
  format?: 'identifier' | 'chat';
}) {
  const dir = useEmojiDirectory();
  const [query, setQuery] = useState('');
  const [guildFilter, setGuildFilter] = useState<string>('all');
  const [unicodeDraft, setUnicodeDraft] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (dir.data?.emojis ?? []).filter((e) => {
      if (guildFilter !== 'all' && e.guildId !== guildFilter) return false;
      if (!q) return true;
      return e.name.toLowerCase().includes(q);
    });
  }, [dir.data, query, guildFilter]);

  return (
    <div className="flex flex-col">
      <div className="border-b p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche nach Emoji-Name…"
            className="h-8 pl-7 text-sm"
          />
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b p-2">
        <button
          type="button"
          onClick={() => setGuildFilter('all')}
          className={cn(
            'shrink-0 rounded px-2 py-1 text-xs',
            guildFilter === 'all'
              ? 'bg-primary/15 text-primary'
              : 'hover:bg-accent'
          )}
        >
          Alle ({dir.data?.emojis.length ?? 0})
        </button>
        {(dir.data?.guilds ?? []).map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => setGuildFilter(g.id)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded px-2 py-1 text-xs',
              guildFilter === g.id
                ? 'bg-primary/15 text-primary'
                : 'hover:bg-accent'
            )}
          >
            {g.iconUrl && (
              <img src={g.iconUrl} alt="" className="h-3.5 w-3.5 rounded-full" />
            )}
            <span className="max-w-[100px] truncate">{g.name}</span>
            <span className="opacity-60">{g.emojiCount}</span>
          </button>
        ))}
      </div>

      <div className="max-h-64 overflow-y-auto p-2">
        {dir.isLoading && (
          <p className="p-4 text-center text-xs text-muted-foreground">Lade…</p>
        )}
        {dir.error && (
          <p className="p-4 text-center text-xs text-destructive">
            {(dir.error as Error).message}
          </p>
        )}
        {!dir.isLoading && filtered.length === 0 && (
          <p className="p-4 text-center text-xs text-muted-foreground">
            Keine Emojis gefunden.
          </p>
        )}
        <div className="grid grid-cols-8 gap-1">
          {filtered.map((e) => (
            <EmojiCell key={e.id} emoji={e} format={format} onPick={onPick} />
          ))}
        </div>
      </div>

      {allowUnicode && (
        <div className="border-t p-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Oder Unicode-Emoji einfügen
          </div>
          <div className="flex gap-1">
            <Input
              value={unicodeDraft}
              onChange={(e) => setUnicodeDraft(e.target.value)}
              placeholder="😀 (max 4 Zeichen)"
              maxLength={8}
              className="h-8 text-sm"
            />
            <Button
              size="sm"
              disabled={!unicodeDraft.trim()}
              onClick={() => onPick(unicodeDraft.trim())}
            >
              OK
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmojiCell({
  emoji,
  format,
  onPick,
}: {
  emoji: EmojiEntry;
  format: 'identifier' | 'chat';
  onPick: (v: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() =>
        onPick(
          format === 'chat'
            ? `<${emoji.animated ? 'a' : ''}:${emoji.name}:${emoji.id}>`
            : emoji.identifier
        )
      }
      title={`:${emoji.name}: — ${emoji.guildName}`}
      className="flex h-9 w-9 items-center justify-center rounded hover:bg-accent"
    >
      <img
        src={emoji.imageUrl}
        alt={emoji.name}
        className="h-6 w-6"
        loading="lazy"
      />
    </button>
  );
}
